import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Plus, ChevronRight,
  Edit2, Trash2, Play, CheckCircle, AlertTriangle,
  History, Code, X, Download, FileText, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { AsyncButton } from '../components/ui/async-button';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const mockPolicies = [
  {
    id: 'pol_standard_onboard',
    name: 'Standard Employee Onboarding',
    type: 'workflow',
    version: '2.1.0',
    status: 'active',
    description: 'Controls standard onboarding workflow for new employees',
    rules: [
      { id: 'rule_001', name: 'HR Record Required', condition: "subject.hr_record.status == 'active'", effect: 'require' },
      { id: 'rule_002', name: 'Manager Must Exist', condition: "subject.manager.status == 'active'", effect: 'require' },
      { id: 'rule_003', name: 'Standard Apps Auto-Approve', condition: "action.apps.all(app => app.tier == 'standard')", effect: 'allow', autoApprove: true },
      { id: 'rule_004', name: 'Sensitive Apps Require Approval', condition: "action.apps.any(app => app.tier == 'sensitive')", effect: 'allow', autoApprove: false }
    ],
    updatedAt: '2026-01-10T14:30:00Z',
    updatedBy: 'admin@acme.com'
  },
  {
    id: 'pol_privileged_access',
    name: 'Privileged Access Control',
    type: 'access',
    version: '1.4.0',
    status: 'active',
    description: 'Controls access to admin and privileged resources',
    rules: [
      { id: 'rule_011', name: 'Manager Approval Required', condition: "resource.tier == 'privileged'", effect: 'require_approval', approvers: ['manager'] },
      { id: 'rule_012', name: 'Security Review for Admin', condition: "resource.tier == 'admin'", effect: 'require_approval', approvers: ['manager', 'security_team'] },
      { id: 'rule_013', name: 'Time-Limited Access', condition: "resource.tier in ['admin', 'privileged']", effect: 'limit', maxDuration: '8h' }
    ],
    updatedAt: '2026-01-08T10:00:00Z',
    updatedBy: 'security@acme.com'
  },
  {
    id: 'pol_offboard_compliance',
    name: 'Offboarding Compliance',
    type: 'workflow',
    version: '3.0.0',
    status: 'active',
    description: 'Ensures compliant offboarding with data transfer and access revocation',
    rules: [
      { id: 'rule_021', name: 'HR Termination Confirmed', condition: "subject.hr_record.termination_date != null", effect: 'require' },
      { id: 'rule_022', name: 'Data Transfer Recipient Valid', condition: "action.transfer_to.status == 'active'", effect: 'require' },
      { id: 'rule_023', name: 'Security Review for Involuntary', condition: "subject.termination_type == 'involuntary'", effect: 'require_approval', approvers: ['security_team'] }
    ],
    updatedAt: '2026-01-12T16:00:00Z',
    updatedBy: 'compliance@acme.com'
  },
  {
    id: 'pol_budget_controls',
    name: 'Budget Controls',
    type: 'budget',
    version: '1.0.0',
    status: 'active',
    description: 'Controls spending limits per workflow and department',
    rules: [
      { id: 'rule_031', name: 'Department Budget Limit', condition: "action.cost <= department.monthly_budget", effect: 'allow' },
      { id: 'rule_032', name: 'Per-User License Limit', condition: "action.per_user_cost <= 100", effect: 'allow' },
      { id: 'rule_033', name: 'High Cost Approval', condition: "action.cost > 500", effect: 'require_approval', approvers: ['finance'] }
    ],
    updatedAt: '2026-01-05T09:00:00Z',
    updatedBy: 'finance@acme.com'
  }
];

const simulationResults = {
  casesEvaluated: 100,
  wouldPass: 94,
  wouldFail: 4,
  wouldRequireApproval: 2,
  impact: [
    { type: 'positive', message: '3 cases would now auto-approve (were manual)' },
    { type: 'negative', message: '1 case would now require approval (was auto)' }
  ]
};

const NewPolicyModal = ({ isOpen, onClose, onCreate, policyName, setPolicyName, policyType, setPolicyType }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="new-policy-modal">
      <div className="bg-white rounded-lg w-full max-w-md p-4 shadow-lg border border-slate-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-900">Create New Policy</h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Policy Name</label>
            <input
              type="text"
              value={policyName}
              onChange={(e) => setPolicyName(e.target.value)}
              placeholder="e.g., Access Control Policy"
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="policy-name-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Policy Type</label>
            <select
              value={policyType}
              onChange={(e) => setPolicyType(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="policy-type-select"
            >
              <option value="workflow">Workflow</option>
              <option value="access">Access</option>
              <option value="budget">Budget</option>
              <option value="security">Security</option>
            </select>
          </div>
        </div>

        <div className="flex items-center justify-between mt-4">
          <Button variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={onCreate} data-testid="create-policy-submit-btn">
            Create Policy
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PolicyStudio = () => {
  const navigate = useNavigate();
  const [selectedPolicy, setSelectedPolicy] = useState(mockPolicies[0]);
  const [showSimulation, setShowSimulation] = useState(false);
  const [activeTab, setActiveTab] = useState('rules');
  const [showNewPolicyModal, setShowNewPolicyModal] = useState(false);
  const [newPolicyName, setNewPolicyName] = useState('');
  const [newPolicyType, setNewPolicyType] = useState('workflow');
  const [isRollingBack, setIsRollingBack] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);
  const [showPublishPreview, setShowPublishPreview] = useState(false);

  const handleCreatePolicy = async () => {
    if (!newPolicyName.trim()) return;
    await new Promise(resolve => setTimeout(resolve, 800));
    toast.success('Policy created', { description: `${newPolicyName} has been created as a draft.` });
    setShowNewPolicyModal(false);
    setNewPolicyName('');
    setNewPolicyType('workflow');
  };

  const handleRollback = async (version) => {
    setIsRollingBack(version);
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Rollback complete', { description: `Policy rolled back to version ${version}.` });
    setIsRollingBack(null);
  };

  const handleViewAffectedCases = () => {
    navigate('/control-tower');
  };

  const handleSaveDraft = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Draft saved', { description: 'Your changes have been saved as a draft.' });
  };

  const handleDeletePolicy = async () => {
    await new Promise(resolve => setTimeout(resolve, 1000));
    toast.success('Policy deleted', { description: `${selectedPolicy?.name} has been deleted.` });
    setShowDeleteConfirm(false);
  };

  const handleExport = async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    const policyData = JSON.stringify(selectedPolicy, null, 2);
    const blob = new Blob([policyData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedPolicy?.id || 'policy'}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Policy exported', { description: 'Policy has been downloaded as JSON.' });
  };

  const handlePublish = async () => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    toast.success('Policy published', { description: `${selectedPolicy?.name} is now live.` });
    setShowPublishPreview(false);
  };

  const handleDeleteRule = async () => {
    await new Promise(resolve => setTimeout(resolve, 500));
    toast.success('Rule deleted', { description: `Rule "${ruleToDelete?.name}" has been removed.` });
    setRuleToDelete(null);
  };

  const getTypeBadge = (type) => {
    const config = {
      workflow: 'badge-status bg-primary/10 text-primary',
      access: 'badge-status bg-slate-200 text-slate-700',
      budget: 'badge-status bg-slate-100 text-slate-600',
      security: 'badge-status bg-slate-900 text-white'
    };
    return config[type] || 'badge-neutral';
  };

  const getEffectBadge = (effect) => {
    const config = {
      require: 'bg-slate-900 text-white',
      allow: 'bg-primary/10 text-primary',
      require_approval: 'bg-slate-200 text-slate-700',
      limit: 'bg-slate-100 text-slate-600'
    };
    return config[effect] || 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="space-y-6" data-testid="policy-studio-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-slate-500" />
            Policy Studio
          </h1>
          <p className="text-sm text-slate-500 mt-1">Create and manage automation policies</p>
        </div>
        <Button onClick={() => setShowNewPolicyModal(true)} size="sm" data-testid="new-policy-btn">
          <Plus className="w-4 h-4 mr-1" />
          New Policy
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-slate-200">
            <div className="px-4 py-3 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-slate-900">Policies ({mockPolicies.length})</h2>
            </div>
            <div className="divide-y divide-slate-100">
              {mockPolicies.map((policy) => (
                <div
                  key={policy.id}
                  onClick={() => setSelectedPolicy(policy)}
                  className={`px-4 py-3 cursor-pointer transition-colors ${selectedPolicy?.id === policy.id ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-900 truncate">{policy.name}</span>
                    <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={getTypeBadge(policy.type)}>
                      {policy.type}
                    </span>
                    <span className="text-xs text-slate-400">v{policy.version}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {selectedPolicy && (
            <>
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-semibold text-slate-900">{selectedPolicy.name}</h2>
                      <span className={getTypeBadge(selectedPolicy.type)}>
                        {selectedPolicy.type}
                      </span>
                      <span className="badge-status badge-active">
                        {selectedPolicy.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{selectedPolicy.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
                      <span>Version {selectedPolicy.version}</span>
                      <span>Updated {new Date(selectedPolicy.updatedAt).toLocaleDateString()}</span>
                      <span>by {selectedPolicy.updatedBy}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <History className="w-4 h-4 text-slate-500" />
                    </button>
                    <button className="p-2 border border-slate-200 rounded-lg hover:bg-slate-50">
                      <Code className="w-4 h-4 text-slate-500" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-slate-200" data-testid="policy-editor">
                <div className="border-b border-slate-100">
                  <div className="flex">
                    {['rules', 'simulation', 'history'].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3 text-sm font-medium capitalize ${activeTab === tab
                          ? 'text-primary border-b-2 border-primary'
                          : 'text-slate-500 hover:text-slate-700'
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="p-4">
                  {activeTab === 'rules' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-900">Rules ({selectedPolicy.rules.length})</h3>
                        <button className="text-sm text-primary hover:underline flex items-center gap-1">
                          <Plus className="w-3 h-3" />
                          Add Rule
                        </button>
                      </div>

                      <div className="space-y-3">
                        {selectedPolicy.rules.map((rule, idx) => (
                          <div
                            key={rule.id}
                            className="border border-slate-100 rounded-lg p-3 hover:border-slate-200 transition-colors"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex items-start gap-3">
                                <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                                  {idx + 1}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-medium text-slate-900">{rule.name}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${getEffectBadge(rule.effect)}`}>
                                      {rule.effect.replace('_', ' ')}
                                    </span>
                                    {rule.autoApprove !== undefined && (
                                      <span className={`text-xs px-1.5 py-0.5 rounded ${rule.autoApprove ? 'bg-primary/10 text-primary' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                        {rule.autoApprove ? 'auto-approve' : 'manual'}
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-2 bg-slate-50 rounded p-2 font-mono text-xs text-slate-600">
                                    {rule.condition}
                                  </div>
                                  {rule.approvers && (
                                    <div className="mt-2 text-xs text-slate-400">
                                      Approvers: {rule.approvers.join(', ')}
                                    </div>
                                  )}
                                  {rule.maxDuration && (
                                    <div className="mt-2 text-xs text-slate-400">
                                      Max duration: {rule.maxDuration}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <button
                                  className="p-1.5 hover:bg-slate-100 rounded"
                                  onClick={() => toast.info('Edit rule', { description: 'Rule editor coming soon' })}
                                >
                                  <Edit2 className="w-3.5 h-3.5 text-slate-400" />
                                </button>
                                <button
                                  className="p-1.5 hover:bg-slate-100 rounded hover:bg-red-50"
                                  onClick={() => setRuleToDelete(rule)}
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-slate-400 hover:text-red-500" />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {activeTab === 'simulation' && (
                    <div className="space-y-4" data-testid="policy-simulation">
                      <div className="flex items-center justify-between">
                        <h3 className="text-sm font-semibold text-slate-900">Policy Simulation</h3>
                        <div className="flex items-center gap-2">
                          <select className="text-sm border border-slate-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary/20">
                            <option>Last 100 cases</option>
                            <option>Last 500 cases</option>
                            <option>Last 1000 cases</option>
                          </select>
                          <Button size="sm" onClick={() => setShowSimulation(true)}>
                            <Play className="w-3 h-3 mr-1" />
                            Run Simulation
                          </Button>
                        </div>
                      </div>

                      {showSimulation && (
                        <div className="border border-slate-200 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-slate-900 mb-4">Simulation Results</h4>

                          <div className="grid grid-cols-4 gap-3 mb-4">
                            <div className="text-center p-3 bg-slate-50 rounded-lg">
                              <p className="text-2xl font-semibold text-slate-900">{simulationResults.casesEvaluated}</p>
                              <span className="text-xs text-slate-400">Cases Evaluated</span>
                            </div>
                            <div className="text-center p-3 bg-primary/10 rounded-lg">
                              <p className="text-2xl font-semibold text-primary">{simulationResults.wouldPass}</p>
                              <span className="text-xs text-primary">Would Pass</span>
                            </div>
                            <div className="text-center p-3 bg-slate-900 rounded-lg">
                              <p className="text-2xl font-semibold text-white">{simulationResults.wouldFail}</p>
                              <span className="text-xs text-slate-300">Would Fail</span>
                            </div>
                            <div className="text-center p-3 bg-slate-200 rounded-lg">
                              <p className="text-2xl font-semibold text-slate-700">{simulationResults.wouldRequireApproval}</p>
                              <span className="text-xs text-slate-600">Need Approval</span>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <h5 className="text-sm font-medium text-slate-700">Impact of Proposed Changes</h5>
                            {simulationResults.impact.map((item, idx) => (
                              <div key={idx} className={`flex items-center gap-2 text-sm p-2 rounded ${item.type === 'positive' ? 'bg-primary/10 text-primary' : 'bg-slate-200 text-slate-700'
                                }`}>
                                {item.type === 'positive' ? (
                                  <CheckCircle className="w-4 h-4" />
                                ) : (
                                  <AlertTriangle className="w-4 h-4" />
                                )}
                                {item.message}
                              </div>
                            ))}
                          </div>

                          <button
                            onClick={handleViewAffectedCases}
                            className="mt-4 text-sm text-primary hover:underline"
                            data-testid="view-affected-cases-btn"
                          >
                            View Affected Cases
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'history' && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-semibold text-slate-900">Version History</h3>
                      <div className="space-y-2">
                        {[
                          { version: '2.1.0', date: '2026-01-10', by: 'admin@acme.com', change: 'Added budget rule', current: true },
                          { version: '2.0.0', date: '2025-12-01', by: 'admin@acme.com', change: 'Revised manager check' },
                          { version: '1.0.0', date: '2025-06-01', by: 'admin@acme.com', change: 'Initial version' }
                        ].map((v, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:border-slate-200 transition-colors">
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-medium ${v.current ? 'text-primary' : 'text-slate-900'}`}>
                                v{v.version}
                              </span>
                              {v.current && (
                                <span className="badge-status badge-active">
                                  current
                                </span>
                              )}
                              <span className="text-sm text-slate-500">{v.change}</span>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span>{v.date}</span>
                              <span>{v.by}</span>
                              {!v.current && (
                                <button
                                  onClick={() => handleRollback(v.version)}
                                  disabled={isRollingBack === v.version}
                                  className="text-primary hover:underline disabled:opacity-50"
                                  data-testid={`rollback-${v.version}-btn`}
                                >
                                  {isRollingBack === v.version ? 'Rolling back...' : 'Rollback'}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between bg-white rounded-lg border border-slate-200 p-4">
                <div className="flex items-center gap-2">
                  <AsyncButton
                    variant="outline"
                    size="sm"
                    onClick={handleSaveDraft}
                    loadingText="Saving..."
                    successMessage="Draft saved"
                  >
                    <FileText className="w-4 h-4 mr-1" />
                    Save Draft
                  </AsyncButton>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Policy
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <AsyncButton
                    variant="outline"
                    size="sm"
                    onClick={handleExport}
                    loadingText="Exporting..."
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Export
                  </AsyncButton>
                  <Button size="sm" onClick={() => setShowPublishPreview(true)}>
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Publish
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <NewPolicyModal
        isOpen={showNewPolicyModal}
        onClose={() => setShowNewPolicyModal(false)}
        onCreate={handleCreatePolicy}
        policyName={newPolicyName}
        setPolicyName={setNewPolicyName}
        policyType={newPolicyType}
        setPolicyType={setNewPolicyType}
      />

      {/* Delete Policy Confirmation */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedPolicy?.name}"? This action cannot be undone.
              Any workflows using this policy will need to be updated.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AsyncButton
              variant="destructive"
              onClick={handleDeletePolicy}
              loadingText="Deleting..."
            >
              Delete Policy
            </AsyncButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Rule Confirmation */}
      <AlertDialog open={!!ruleToDelete} onOpenChange={(open) => !open && setRuleToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the rule "{ruleToDelete?.name}"? This will affect policy behavior.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AsyncButton
              variant="destructive"
              onClick={handleDeleteRule}
              loadingText="Deleting..."
            >
              Delete Rule
            </AsyncButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Publish Preview Dialog */}
      <Dialog open={showPublishPreview} onOpenChange={setShowPublishPreview}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Publish Policy</DialogTitle>
            <DialogDescription>
              Review the changes before publishing "{selectedPolicy?.name}" to production.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <h4 className="text-sm font-medium text-foreground mb-2">Policy Summary</h4>
              <div className="space-y-1 text-sm text-muted-foreground">
                <p><span className="font-medium">Name:</span> {selectedPolicy?.name}</p>
                <p><span className="font-medium">Type:</span> {selectedPolicy?.type}</p>
                <p><span className="font-medium">Rules:</span> {selectedPolicy?.rules?.length || 0} rules configured</p>
                <p><span className="font-medium">Version:</span> {selectedPolicy?.version} → {selectedPolicy?.version?.split('.').map((v, i) => i === 2 ? parseInt(v) + 1 : v).join('.')}</p>
              </div>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> Publishing will make this policy active immediately. All matching requests will be evaluated against these rules.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishPreview(false)}>Cancel</Button>
            <AsyncButton
              onClick={handlePublish}
              loadingText="Publishing..."
              successMessage="Policy published successfully"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Publish to Production
            </AsyncButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PolicyStudio;
